// Chemin: C:\gestion-copro\src\pages\admin\SentinelleCSPage.tsx
// Page SENTINELLE CS — Chat plein écran (sas d'entrée unique — principe ESCALATOR)
// Session 214 : Création — dérivée de SentinelleVigiePage.tsx, adaptée au contexte CS
//               Différences vs VIGIE :
//               - Pas d'onboarding (le CS est déjà configuré)
//               - Pas d'alerte quota (quota géré côté abonnement CS, pas affiché inline)
//               - Pas de suggestions bailleur (profil bailleur = VIGIE uniquement)
//               - Archivage direct via uploadDocument() (pas de tunnel AjouterDocumentVigiePage)
//               - Navigation vers /admin/courriers, /admin/signalements, /admin/dossiers
//               - Source archivage : sentinelle_cs

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, Timestamp, serverTimestamp, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  Send,
  Shield,
  AlertTriangle,
  CheckCircle,
  Loader2,
  X,
  Archive,
  FileText,
  Upload,
  Paperclip,
  RotateCcw,
  Copy,
  Sparkles,
  FolderOpen,
  Calendar,
  CreditCard,
  Download
} from 'lucide-react';

// Services existants
import { verifierFichier } from '../../services/gardien.service';
import {
  isFileAccepted,
  uploadTempFile,
  deleteTempFile,
  analyzeDocument,
  computeFileHash,
  type DocumentSuggestion
} from '../../services/documentAnalysis.service';

// Chat SENTINELLE
import { askAssistant, fetchSuggestions, AssistantResult, SuggestionsResult } from '../../services/assistant.service';
import { ChatMessage, ProposedAction, CourrierGenere } from '../../types/chat.types';
import SentinelleAvatar from '../../components/chat/SentinelleAvatar';
import { renderMarkdown } from '../../components/chat/ChatMessage';
import BoutonDictee from '../../components/chat/BoutonDictee';

// Session 141 : Persistance conversation Firestore
import {
  getActiveConversation,
  createConversation,
  updateConversationMessages,
  deleteConversation,
  convertFirestoreMessages
} from '../../services/conversation.service';

// Vérification doublons + archivage centralisé
import { checkDuplicateByHash, uploadDocument, type DuplicateCheckResult } from '../../services/document.service';

// Création événement calendrier depuis chat
import { creerEcheance } from '../../services/echeance.service';
// Export PDF résultats structurés SENTINELLE
import { isResultatStructure, telechargerPDFSentinelle } from '../../services/pdfSentinelle.service';

// ============================================================================
// TYPES LOCAUX
// ============================================================================

interface ResultatAnalyse {
  suggestion: DocumentSuggestion;
  fileName: string;
  fileUrl: string;
  filePath: string;
  fileType: string;
  fileSize: number;
  fileHash: string;
  fileOriginal?: File;
}

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const SentinelleCSPage: React.FC = () => {
  const { firebaseUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const { coproprieteId } = useCopropriete();
  const prenom = userProfile?.prenom || 'vous';

  // --- Contexte dossier (injection depuis AffaireDetailPage) ---
  const [searchParams, setSearchParams] = useSearchParams();
  const [contexteDossier, setContexteDossier] = useState<{
    id: string;
    titre: string;
    type?: string;
    description?: string;
  } | null>(null);
  const [contexteInjecte, setContexteInjecte] = useState(false);

  // --- Contexte signalement (injection depuis SignalementDetailPage) ---
  const [contexteSignalement, setContexteSignalement] = useState<{
    id: string;
    titre: string;
    description?: string;
    categorie?: string;
    urgence?: string;
    localisation?: string;
    localisationDetail?: string;
    statut?: string;
  } | null>(null);
  const [contexteSignalementInjecte, setContexteSignalementInjecte] = useState(false);
  const [affaireIdPourSignalement, setAffaireIdPourSignalement] = useState<string | null>(null);

  // --- Contexte relance (injection depuis CalendrierPage) ---
  const [contexteRelance, setContexteRelance] = useState<{
    courrierId: string;
    titre: string;
  } | null>(null);
  const [contexteRelanceInjecte, setContexteRelanceInjecte] = useState(false);

  // --- Chat SENTINELLE ---
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // --- Persistance conversation ---
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationLoaded, setConversationLoaded] = useState(false);
  const savingRef = useRef(false);

  // --- Analyse document (intégrée dans le chat) ---
  const [isDragOver, setIsDragOver] = useState(false);
  const [analyseEnCours, setAnalyseEnCours] = useState(false);
  const [etapeAnalyse, setEtapeAnalyse] = useState('');
  const [resultatAnalyse, setResultatAnalyse] = useState<ResultatAnalyse | null>(null);
  const [erreurAnalyse, setErreurAnalyse] = useState<string | null>(null);
  const [archivageEnCours, setArchivageEnCours] = useState(false);
  const [doublonDetecte, setDoublonDetecte] = useState<DuplicateCheckResult | null>(null);

  // --- Suggestions dynamiques accueil ---
  const [suggestionsAccueil, setSuggestionsAccueil] = useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ============================================================================
  // PERSISTANCE
  // ============================================================================

  const sauvegarderConversation = useCallback(async (messages: ChatMessage[], currentConversationId: string | null) => {
    if (!coproprieteId || !firebaseUser || savingRef.current) return;
    if (messages.length === 0) return;

    savingRef.current = true;
    try {
      if (currentConversationId) {
        await updateConversationMessages(coproprieteId, currentConversationId, messages);
      } else {
        const newId = await createConversation(coproprieteId, firebaseUser.uid, messages);
        setConversationId(newId);
      }
    } catch (error) {
      console.error('[SENTINELLE CS] Erreur sauvegarde conversation:', error);
    } finally {
      savingRef.current = false;
    }
  }, [coproprieteId, firebaseUser]);

  // ============================================================================
  // EFFETS
  // ============================================================================

  // Charger la conversation active au montage
  useEffect(() => {
    if (!coproprieteId || !firebaseUser || conversationLoaded) return;

    // Ne pas charger si on arrive avec un contexte (nouvelle conversation forcée)
    const contexte = searchParams.get('contexte');
    if (contexte) {
      setConversationLoaded(true);
      return;
    }

    const chargerConversation = async () => {
      try {
        const conv = await getActiveConversation(coproprieteId, firebaseUser.uid);
        if (conv && conv.messages && conv.messages.length > 0) {
          const messages = convertFirestoreMessages(conv.messages);
          setChatMessages(messages);
          setConversationId(conv.id);
        }
      } catch (error) {
        console.error('[SENTINELLE CS] Erreur chargement conversation:', error);
      } finally {
        setConversationLoaded(true);
      }
    };

    chargerConversation();
  }, [coproprieteId, firebaseUser, conversationLoaded, searchParams]);

  // Charger les suggestions dynamiques à l'ouverture
  useEffect(() => {
    if (!coproprieteId) return;

    const chargerSuggestions = async () => {
      setSuggestionsLoading(true);
      try {
        const result = await fetchSuggestions(coproprieteId);
        if (Array.isArray(result)) {
          if (result.length > 0) setSuggestionsAccueil(result);
        } else if (result && typeof result === 'object') {
          if (result.suggestions && result.suggestions.length > 0) {
            setSuggestionsAccueil(result.suggestions);
          }
        }
      } catch (error) {
        console.error('[SENTINELLE CS] Erreur chargement suggestions:', error);
      } finally {
        setSuggestionsLoading(false);
      }
    };

    chargerSuggestions();
  }, [coproprieteId]);

  // Lire les paramètres URL pour injection de contexte
  useEffect(() => {
    const contexte = searchParams.get('contexte');
    if (contexte === 'dossier') {
      const id = searchParams.get('id');
      const titre = searchParams.get('titre');
      if (id && titre) {
        setContexteDossier({
          id,
          titre: decodeURIComponent(titre),
          type: searchParams.get('type') || undefined,
          description: searchParams.get('description') ? decodeURIComponent(searchParams.get('description')!) : undefined,
        });
      }
      setSearchParams({}, { replace: true });
    } else if (contexte === 'signalement') {
      const id = searchParams.get('id');
      const titre = searchParams.get('titre');
      if (id && titre) {
        setContexteSignalement({
          id,
          titre: decodeURIComponent(titre),
          description: searchParams.get('description') ? decodeURIComponent(searchParams.get('description')!) : undefined,
          categorie: searchParams.get('categorie') ? decodeURIComponent(searchParams.get('categorie')!) : undefined,
          urgence: searchParams.get('urgence') || undefined,
          localisation: searchParams.get('localisation') ? decodeURIComponent(searchParams.get('localisation')!) : undefined,
          localisationDetail: searchParams.get('localisationDetail') ? decodeURIComponent(searchParams.get('localisationDetail')!) : undefined,
          statut: searchParams.get('statut') ? decodeURIComponent(searchParams.get('statut')!) : undefined,
        });

        if (coproprieteId) {
          const affairesQuery = query(
            collection(db, 'affaires'),
            where('coproprieteId', '==', coproprieteId),
            where('signalementIds', 'array-contains', id)
          );
          getDocs(affairesQuery).then(snapshot => {
            if (!snapshot.empty) {
              setAffaireIdPourSignalement(snapshot.docs[0].id);
            }
          }).catch(() => { /* non bloquant */ });
        }
      }
      setSearchParams({}, { replace: true });
    } else if (contexte === 'relance') {
      const courrierId = searchParams.get('courrierId');
      const titre = searchParams.get('titre');
      if (courrierId && titre) {
        setContexteRelance({ courrierId, titre: decodeURIComponent(titre) });
      }
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Injection automatique contexte dossier
  useEffect(() => {
    if (!contexteDossier || contexteInjecte) return;
    const messageContexte = contexteDossier.description
      ? `Je travaille sur le dossier "${contexteDossier.titre}". Contexte : ${contexteDossier.description}. Que me conseilles-tu ?`
      : `Je travaille sur le dossier "${contexteDossier.titre}". Que me conseilles-tu ?`;
    setContexteInjecte(true);
    envoyerMessageChat(messageContexte);
  }, [contexteDossier, contexteInjecte]);

  // Injection automatique contexte signalement
  useEffect(() => {
    if (!contexteSignalement || contexteSignalementInjecte) return;
    const parties: string[] = [];
    parties.push(`J'ai un signalement : "${contexteSignalement.titre}".`);
    if (contexteSignalement.description) parties.push(`Description : ${contexteSignalement.description}`);
    if (contexteSignalement.categorie) parties.push(`Catégorie : ${contexteSignalement.categorie}.`);
    if (contexteSignalement.urgence) parties.push(`Urgence : ${contexteSignalement.urgence}.`);
    if (contexteSignalement.localisation) {
      const locDetail = contexteSignalement.localisationDetail ? ` (${contexteSignalement.localisationDetail})` : '';
      parties.push(`Localisation : ${contexteSignalement.localisation}${locDetail}.`);
    }
    if (contexteSignalement.statut) parties.push(`Statut actuel : ${contexteSignalement.statut}.`);
    parties.push('Aide-moi à rédiger un courrier adapté à cette situation.');
    setContexteSignalementInjecte(true);
    envoyerMessageChat(parties.join(' '));
  }, [contexteSignalement, contexteSignalementInjecte]);

  // Injection automatique contexte relance
  useEffect(() => {
    if (!contexteRelance || contexteRelanceInjecte) return;
    const messageRelance = `Le courrier "${contexteRelance.titre}" est arrivé à échéance sans réponse. Prépare-moi une relance adaptée.`;
    setContexteRelanceInjecte(true);
    envoyerMessageChat(messageRelance);
  }, [contexteRelance, contexteRelanceInjecte]);

  // Auto-scroll vers le dernier message
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, chatLoading, resultatAnalyse, analyseEnCours]);

  // ============================================================================
  // ANALYSE DOCUMENT
  // ============================================================================

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) lancerAnalyse(files[0]);
  }, [coproprieteId]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) lancerAnalyse(files[0]);
    e.target.value = '';
  }, [coproprieteId]);

  const lancerAnalyse = async (file: File) => {
    if (!coproprieteId || !firebaseUser) return;

    setResultatAnalyse(null);
    setErreurAnalyse(null);
    setDoublonDetecte(null);
    setAnalyseEnCours(true);

    try {
      // Validation fichier
      const validation = verifierFichier(file);
      if (!validation.accepte) {
        setErreurAnalyse(validation.erreur || 'Fichier non accepté.');
        setAnalyseEnCours(false);
        return;
      }
      if (!isFileAccepted(file)) {
        setErreurAnalyse('Format non supporté. Formats acceptés : PDF, images, Word, Excel.');
        setAnalyseEnCours(false);
        return;
      }

      // Étape 1 : Vérification doublon par hash
      setEtapeAnalyse('Vérification du document...');
      const fileHash = await computeFileHash(file);
      const duplicationResult = await checkDuplicateByHash(coproprieteId, fileHash);

      if (duplicationResult.isDuplicate) {
        setDoublonDetecte(duplicationResult);
        setResultatAnalyse({
          suggestion: {} as DocumentSuggestion,
          fileName: file.name,
          fileUrl: '',
          filePath: '',
          fileType: file.type,
          fileSize: file.size,
          fileHash,
          fileOriginal: file
        });
        setAnalyseEnCours(false);
        setEtapeAnalyse('');
        return;
      }

      // Étape 2 : Upload temporaire
      setEtapeAnalyse('Envoi du document à SENTINELLE...');
      const { url, path } = await uploadTempFile(file, coproprieteId, firebaseUser.uid);

      // Étape 3 : Analyse IA
      setEtapeAnalyse('SENTINELLE analyse le document...');
      const resultat = await analyzeDocument(url, file.name, file.type, coproprieteId);

      if (resultat.success && resultat.suggestion) {
        setResultatAnalyse({
          suggestion: resultat.suggestion,
          fileName: file.name,
          fileUrl: url,
          filePath: path,
          fileType: file.type,
          fileSize: file.size,
          fileHash
        });
      } else {
        setErreurAnalyse(resultat.error || 'Analyse impossible. Réessayez ou archivez manuellement.');
      }
    } catch (error: unknown) {
      console.error('[SENTINELLE CS] Erreur analyse document:', error);
      const message = error instanceof Error ? error.message : 'Erreur inattendue';
      if (message.includes('quota') || message.includes('Quota')) {
        setErreurAnalyse('Quota SENTINELLE épuisé.');
      } else {
        setErreurAnalyse(`Erreur lors de l'analyse : ${message}`);
      }
    } finally {
      setAnalyseEnCours(false);
      setEtapeAnalyse('');
    }
  };

  // Archivage CS — direct via uploadDocument() (pas de tunnel intermédiaire)
  const archiverDocument = async () => {
    if (!resultatAnalyse || !coproprieteId || !firebaseUser) return;

    setArchivageEnCours(true);
    try {
      const { suggestion, fileName, fileUrl, fileType, fileSize, fileHash } = resultatAnalyse;
      const userNom = `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim();

      await uploadDocument({
        coproprieteId,
        titre: suggestion.titre,
        type: suggestion.typeDocument,
        categorie: suggestion.categorie || 'administratif',
        description: suggestion.description || undefined,
        dateDocument: suggestion.dateDocument ? new Date(suggestion.dateDocument) : undefined,
        interlocuteurId: suggestion.interlocuteurId || undefined,
        interlocuteurNom: suggestion.interlocuteurNom || undefined,
        affaireId: suggestion.affaireId || undefined,
        visibilite: 'proprietaire',
        userId: firebaseUser.uid,
        userNom,
        fileHash,
        tempFilePath: fileUrl,
        fichierNom: fileName,
        fichierTaille: fileSize,
        fichierType: fileType,
        source: 'sentinelle_cs',
        typeDocumentLabel: suggestion.typeDocumentLabel,
        donneesCles: suggestion.donneesCles || {},
        anomalies: suggestion.anomalies || [],
        analyseSentinelle: {
          confiance: suggestion.confidence,
          explication: suggestion.explication,
          actionsRecommandees: suggestion.actionsRecommandees,
          anomalies: suggestion.anomalies || [],
          donneesCles: suggestion.donneesCles || {},
          dateAnalyse: Timestamp.now()
        },
        sensCorrespondance: suggestion.sensCorrespondance || null,
        courrierId_lie: suggestion.courrierId_lie || undefined,
        courrierRef_lie: suggestion.courrierRef_lie || undefined,
        typeCorrespondance: suggestion.typeCorrespondance || undefined,
        qualiteReponse: suggestion.qualiteReponse || undefined,
        pointsTraites: suggestion.pointsTraites || undefined,
        pointsManquants: suggestion.pointsManquants || undefined,
        alerteQualite: suggestion.alerteQualite || undefined,
      });

      // Message post-archivage
      const postMsg: ChatMessage = {
        id: `archive_ok_${Date.now()}`,
        role: 'assistant' as const,
        timestamp: new Date(),
        content: `"${suggestion.titre}" est archivé dans vos documents. ${suggestion.actionsRecommandees && suggestion.actionsRecommandees.length > 0 ? `\n\nActions recommandées :\n${suggestion.actionsRecommandees.map((a: string) => `• ${a}`).join('\n')}` : ''}`,
        actions: []
      };
      setChatMessages(prev => {
        const updated = [...prev, postMsg];
        sauvegarderConversation(updated, conversationId);
        return updated;
      });

      setResultatAnalyse(null);
      setDoublonDetecte(null);
    } catch (error) {
      console.error('[SENTINELLE CS] Erreur archivage:', error);
      const errMsg = error instanceof Error ? error.message : '';
      if (errMsg.startsWith('DOUBLON_DETECTE|')) {
        const parts = errMsg.split('|');
        setErreurAnalyse(parts[1] || 'Ce document existe déjà dans vos documents.');
      } else {
        setErreurAnalyse('Erreur lors de l\'archivage. Réessayez.');
      }
    } finally {
      setArchivageEnCours(false);
    }
  };

  // "Archiver quand même" après doublon
  const confirmerArchivageMalgreDoublon = async () => {
    if (!resultatAnalyse || !coproprieteId || !firebaseUser) return;

    const file = resultatAnalyse.fileOriginal;
    if (!file) {
      setErreurAnalyse('Déposez à nouveau le document pour l\'archiver.');
      setResultatAnalyse(null);
      setDoublonDetecte(null);
      return;
    }

    setDoublonDetecte(null);
    setResultatAnalyse(null);
    setAnalyseEnCours(true);

    try {
      setEtapeAnalyse('Envoi du document à SENTINELLE...');
      const { url } = await uploadTempFile(file, coproprieteId, firebaseUser.uid);

      setEtapeAnalyse('SENTINELLE analyse le document...');
      const resultat = await analyzeDocument(url, file.name, file.type, coproprieteId);

      if (resultat.success && resultat.suggestion) {
        const { suggestion } = resultat;
        const fileHash = await computeFileHash(file);
        const userNom = `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim();

        await uploadDocument({
          coproprieteId,
          titre: suggestion.titre,
          type: suggestion.typeDocument,
          categorie: suggestion.categorie || 'administratif',
          description: suggestion.description || undefined,
          dateDocument: suggestion.dateDocument ? new Date(suggestion.dateDocument) : undefined,
          interlocuteurId: suggestion.interlocuteurId || undefined,
          interlocuteurNom: suggestion.interlocuteurNom || undefined,
          affaireId: suggestion.affaireId || undefined,
          visibilite: 'proprietaire',
          userId: firebaseUser.uid,
          userNom,
          fileHash,
          tempFilePath: url,
          fichierNom: file.name,
          fichierTaille: file.size,
          fichierType: file.type,
          source: 'sentinelle_cs',
          typeDocumentLabel: suggestion.typeDocumentLabel,
          donneesCles: suggestion.donneesCles || {},
          anomalies: suggestion.anomalies || [],
          analyseSentinelle: {
            confiance: suggestion.confidence,
            explication: suggestion.explication,
            actionsRecommandees: suggestion.actionsRecommandees,
            anomalies: suggestion.anomalies || [],
            donneesCles: suggestion.donneesCles || {},
            dateAnalyse: Timestamp.now()
          },
          sensCorrespondance: suggestion.sensCorrespondance || null,
          courrierId_lie: suggestion.courrierId_lie || undefined,
          courrierRef_lie: suggestion.courrierRef_lie || undefined,
          typeCorrespondance: suggestion.typeCorrespondance || undefined,
          qualiteReponse: suggestion.qualiteReponse || undefined,
          pointsTraites: suggestion.pointsTraites || undefined,
          pointsManquants: suggestion.pointsManquants || undefined,
          alerteQualite: suggestion.alerteQualite || undefined,
          skipDuplicateCheck: true,
        });

        const postMsg: ChatMessage = {
          id: `archive_ok_${Date.now()}`,
          role: 'assistant' as const,
          timestamp: new Date(),
          content: `"${suggestion.titre}" est archivé dans vos documents.`,
          actions: []
        };
        setChatMessages(prev => {
          const updated = [...prev, postMsg];
          sauvegarderConversation(updated, conversationId);
          return updated;
        });
      } else {
        setErreurAnalyse(resultat.error || 'Analyse impossible. Réessayez.');
      }
    } catch (error) {
      console.error('[SENTINELLE CS] Erreur archivage malgré doublon:', error);
      setErreurAnalyse('Erreur lors de l\'archivage. Réessayez.');
    } finally {
      setAnalyseEnCours(false);
      setEtapeAnalyse('');
    }
  };

  const fermerResultat = async () => {
    if (resultatAnalyse?.filePath) {
      try { await deleteTempFile(resultatAnalyse.filePath); } catch { /* Ignorer */ }
    }
    setResultatAnalyse(null);
    setDoublonDetecte(null);
  };

  // ============================================================================
  // CHAT SENTINELLE
  // ============================================================================

  const envoyerMessageChat = async (message: string) => {
    if (!message.trim() || chatLoading || !coproprieteId) return;

    const userMsg: ChatMessage = {
      id: `msg_${Date.now()}_user`,
      role: 'user',
      content: message.trim(),
      timestamp: new Date(),
    };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);
    setChatError(null);

    try {
      const conversationHistory = chatMessages.map(m => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }));

      const result: AssistantResult = await askAssistant(
        message.trim(),
        conversationHistory,
        coproprieteId
      );

      const assistantMsg: ChatMessage = {
        id: `msg_${Date.now()}_assistant`,
        role: 'assistant',
        content: result.message,
        timestamp: new Date(),
        actions: result.actions,
        incitationPieces: result.incitationPieces,
        suggestionsReponse: result.suggestionsReponse,
        // Session 214 : Courrier généré — source fiable (jamais tronqué)
        courrier: result.courrier,
      };

      setChatMessages(prev => {
        const updated = [...prev, assistantMsg];
        sauvegarderConversation(updated, conversationId);
        return updated;
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inattendue';
      if (errorMessage.includes('quota') || errorMessage.includes('Quota')) {
        setChatError('Quota SENTINELLE épuisé.');
      } else {
        setChatError('Erreur de communication avec SENTINELLE. Réessayez.');
      }
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      envoyerMessageChat(chatInput);
    }
  };

  // Détection intelligente du type de destinataire
  const detecterDestinataire = (data: any): string => {
    const typeExplicite = data?.destinataireType;
    if (typeExplicite && typeExplicite !== 'autre') return typeExplicite;
    const nom = (data?.destinataireNom || data?.destinataire || '').toLowerCase();
    if (!nom) return 'autre';
    if (nom.includes('syndic') || nom.includes('foncia') || nom.includes('nexity') || nom.includes('citya')) return 'syndic';
    if (nom.includes('promoteur') || nom.includes('constructeur') || nom.includes('kic')) return 'promoteur';
    if (nom.includes('assur') || nom.includes('maif') || nom.includes('axa') || nom.includes('allianz')) return 'assurance';
    if (nom.includes('mairie') || nom.includes('préfecture') || nom.includes('commune')) return 'administration';
    if (nom.includes('expert') || nom.includes('bureau')) return 'expert';
    return 'autre';
  };

  const extraireNomDestinataire = (data: any, actionLabel: string): string => {
    if (data?.destinataireNom && !data.destinataireNom.match(/^[a-zA-Z0-9]{15,}$/)) return data.destinataireNom;
    if (data?.destinataire && !data.destinataire.match(/^[a-zA-Z0-9]{15,}$/)) return data.destinataire;
    // Extraire depuis le label de l'action (ex: "Écrire au syndic Foncia" → "Foncia")
    const match = actionLabel.match(/(?:au|à|pour|vers)\s+(.+)$/i);
    if (match) return match[1];
    return '';
  };

  // Nettoyage markdown pour le contenu courrier
  const nettoyerMarkdownCourrier = (texte: string): string => {
    return texte
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/^\s*[-*+]\s+/gm, '- ')
      .trim();
  };

  // Détection type de courrier depuis le contenu
  const detecterTypeCourrier = (objet: string, contenu: string): string => {
    const texte = (objet + ' ' + contenu).toLowerCase();
    if (texte.includes('notification') || texte.includes('vous informe') || texte.includes('vous notifions')) return 'notification';
    if (texte.includes('relance') || texte.includes('rappel') || texte.includes('sans réponse')) return 'relance';
    if (texte.includes('mise en demeure') || texte.includes('dernier délai')) return 'mise_en_demeure';
    if (texte.includes('compte rendu') || texte.includes('procès-verbal')) return 'compte_rendu';
    return 'demande_information';
  };

  // Gestion des actions proposées par SENTINELLE
  const handleAction = async (action: ProposedAction, sourceCourrier?: CourrierGenere) => {
    if (action.type === 'generate_courrier') {
      const d = action.data as any;
      // Session 214 : Utiliser sourceCourrier (depuis msg.courrier) si disponible — jamais tronqué
      const contenuFinal = sourceCourrier?.contenu || d?.contenu || '';
      const objetFinal = sourceCourrier?.objet || d?.objet || '';
      const nomDestinataire = extraireNomDestinataire(sourceCourrier || d, action.label);
      const affaireIdResolue = affaireIdPourSignalement || contexteDossier?.id || d?.affaireId || null;
      navigate('/admin/courriers/nouveau', {
        state: {
          fromSentinelle: true,
          prefill: {
            objet: objetFinal,
            contenu: nettoyerMarkdownCourrier(contenuFinal),
            type: d?.type || detecterTypeCourrier(d?.objet || '', d?.contenu || ''),
            destinataire: nomDestinataire,
            destinataireType: detecterDestinataire({ ...d, destinataireNom: nomDestinataire, destinataire: nomDestinataire }),
            modeEnvoi: d?.modeEnvoiSuggere || 'email',
            ...(contexteSignalement?.id && { signalementId: contexteSignalement.id }),
            ...(affaireIdResolue && { affaireId: affaireIdResolue }),
            ...(Array.isArray(d?.destinatairesCC) && d.destinatairesCC.length > 0 && { destinatairesCC: d.destinatairesCC }),
          }
        }
      });
    } else if (action.type === 'send_email') {
      const d = action.data as any;
      const contenuFinal = sourceCourrier?.contenu || d?.contenu || '';
      const objetFinal = sourceCourrier?.objet || d?.objet || '';
      const nomDestinataire = extraireNomDestinataire(sourceCourrier || d, action.label);
      const affaireIdResolue = affaireIdPourSignalement || contexteDossier?.id || d?.affaireId || null;
      navigate('/admin/courriers/nouveau', {
        state: {
          fromSentinelle: true,
          prefill: {
            objet: objetFinal,
            contenu: nettoyerMarkdownCourrier(contenuFinal),
            type: d?.type || detecterTypeCourrier(d?.objet || '', d?.contenu || ''),
            destinataire: nomDestinataire,
            destinataireType: detecterDestinataire({ ...d, destinataireNom: nomDestinataire, destinataire: nomDestinataire }),
            modeEnvoi: 'email',
            ...(contexteSignalement?.id && { signalementId: contexteSignalement.id }),
            ...(affaireIdResolue && { affaireId: affaireIdResolue }),
            ...(Array.isArray(d?.destinatairesCC) && d.destinatairesCC.length > 0 && { destinatairesCC: d.destinatairesCC }),
          }
        }
      });
    } else if (action.type === 'create_signalement') {
      const d = action.data as any;
      navigate('/admin/signalements/nouveau', {
        state: {
          fromSentinelle: true,
          prefill: {
            titre: d?.titre || '',
            description: d?.description || '',
            categorie: d?.categorie || '',
            localisation: d?.localisation || '',
          }
        }
      });
    } else if (action.type === 'create_affaire') {
      const d = action.data as any;
      const params = new URLSearchParams();
      if (d?.titre) params.set('titre', d.titre);
      if (d?.type) params.set('type', d.type);
      if (d?.description) params.set('description', d.description);
      if (d?.priorite) params.set('priorite', d.priorite);
      if (d?.garantie) params.set('garantie', d.garantie);
      const queryString = params.toString();
      navigate(`/admin/affaires/nouvelle${queryString ? '?' + queryString : ''}`);
    } else if (action.type === 'update_paiement') {
      const d = action.data as any;
      if (!d?.documentId) return;
      try {
        const docRef = doc(db, 'documents', d.documentId);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) return;
        const existingDonneesCles = (docSnap.data().donneesCles || {}) as Record<string, string>;
        await updateDoc(docRef, {
          donneesCles: {
            ...existingDonneesCles,
            statutPaiement: 'oui',
            datePaiement: d.datePaiement || new Date().toISOString().split('T')[0],
            modePaiement: d.modePaiement || '',
            referencePaiement: d.reference || ''
          },
          updatedAt: Timestamp.now()
        });
        const confirmMsg: ChatMessage = {
          id: `paiement_ok_${Date.now()}`,
          role: 'assistant' as const,
          timestamp: new Date(),
          content: `Paiement enregistré. Le document est maintenant marqué comme réglé (${d.modePaiement || 'mode non précisé'}${d.datePaiement ? ', le ' + new Date(d.datePaiement).toLocaleDateString('fr-FR') : ''}).`,
          actions: []
        };
        setChatMessages(prev => {
          const updated = [...prev, confirmMsg];
          sauvegarderConversation(updated, conversationId);
          return updated;
        });
      } catch (error) {
        console.error('[SENTINELLE CS] Erreur update_paiement:', error);
      }
    } else if (action.type === 'create_evenement') {
      const d = action.data as any;
      if (!coproprieteId) return;
      try {
        let dateEcheance: Date;
        if (d?.date) {
          dateEcheance = new Date(d.date);
          if (isNaN(dateEcheance.getTime())) {
            dateEcheance = new Date();
            dateEcheance.setDate(dateEcheance.getDate() + 7);
          }
        } else {
          dateEcheance = new Date();
          dateEcheance.setDate(dateEcheance.getDate() + 7);
        }

        await creerEcheance({
          coproprieteId,
          titre: d?.titre || action.label || 'Échéance SENTINELLE',
          description: d?.description || '',
          dateEcheance: Timestamp.fromDate(dateEcheance),
          source: 'manuelle',
          sourceLabel: 'SENTINELLE',
          terminee: false,
        });

        const confirmMsg: ChatMessage = {
          id: `evenement_ok_${Date.now()}`,
          role: 'assistant' as const,
          timestamp: new Date(),
          content: `Échéance créée : "${d?.titre || action.label}" programmée au ${dateEcheance.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}. Vous la retrouverez dans le calendrier.`,
          actions: []
        };
        setChatMessages(prev => {
          const updated = [...prev, confirmMsg];
          sauvegarderConversation(updated, conversationId);
          return updated;
        });
      } catch (error) {
        console.error('[SENTINELLE CS] Erreur create_evenement:', error);
        const errMsg: ChatMessage = {
          id: `evenement_err_${Date.now()}`,
          role: 'assistant' as const,
          timestamp: new Date(),
          content: "Erreur lors de la création de l'échéance. Vous pouvez la créer manuellement depuis le calendrier.",
          actions: []
        };
        setChatMessages(prev => {
          const updated = [...prev, errMsg];
          sauvegarderConversation(updated, conversationId);
          return updated;
        });
      }
    } else if (action.type === 'create_interlocuteur') {
      const d = action.data as any;
      if (!coproprieteId) return;
      try {
        const nomComplet = [d?.prenom, d?.nom].filter(Boolean).join(' ') || 'Contact';
        const contactPrincipal: Record<string, any> = { nom: d?.nom || nomComplet, principal: true };
        if (d?.prenom) contactPrincipal.prenom = d.prenom;
        if (d?.fonction) contactPrincipal.fonction = d.fonction;
        if (d?.email) contactPrincipal.email = d.email;
        if (d?.telephone) contactPrincipal.telephone = d.telephone;

        const { createInterlocuteur } = await import('../../services/interlocuteur.service');
        await createInterlocuteur({
          coproprieteId,
          type: d?.type || 'autre',
          raisonSociale: d?.societe || nomComplet,
          contacts: [contactPrincipal],
          actif: true,
        } as any);

        const confirmMsg: ChatMessage = {
          id: `interlocuteur_ok_${Date.now()}`,
          role: 'assistant' as const,
          timestamp: new Date(),
          content: `Contact ajouté : ${nomComplet}${d?.fonction ? ' — ' + d.fonction : ''}. Vous le retrouverez dans Interlocuteurs.`,
          actions: []
        };
        setChatMessages(prev => {
          const updated = [...prev, confirmMsg];
          sauvegarderConversation(updated, conversationId);
          return updated;
        });
      } catch (error) {
        console.error('[SENTINELLE CS] Erreur create_interlocuteur:', error);
        const errMsg: ChatMessage = {
          id: `interlocuteur_err_${Date.now()}`,
          role: 'assistant' as const,
          timestamp: new Date(),
          content: "Erreur lors de la création du contact. Vous pouvez l'ajouter manuellement depuis Interlocuteurs.",
          actions: []
        };
        setChatMessages(prev => {
          const updated = [...prev, errMsg];
          sauvegarderConversation(updated, conversationId);
          return updated;
        });
      }
    }
  };

  // Reset chat + suppression conversation Firestore
  const resetChat = async () => {
    if (resultatAnalyse?.filePath) {
      try { await deleteTempFile(resultatAnalyse.filePath); } catch { /* Ignorer */ }
    }
    if (conversationId && coproprieteId) {
      try { await deleteConversation(coproprieteId, conversationId); } catch (error) {
        console.error('[SENTINELLE CS] Erreur suppression conversation:', error);
      }
    }
    setChatMessages([]);
    setChatError(null);
    setResultatAnalyse(null);
    setErreurAnalyse(null);
    setDoublonDetecte(null);
    setAnalyseEnCours(false);
    setEtapeAnalyse('');
    setContexteDossier(null);
    setContexteInjecte(false);
    setContexteSignalement(null);
    setContexteSignalementInjecte(false);
    setAffaireIdPourSignalement(null);
    setContexteRelance(null);
    setContexteRelanceInjecte(false);
    setConversationId(null);
    setConversationLoaded(false);
  };

  // ============================================================================
  // RENDU
  // ============================================================================

  if (!coproprieteId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Impossible de charger SENTINELLE</p>
          <p className="text-sm text-gray-400 mt-1">Aucune copropriété n'est associée à votre compte.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white rounded-xl shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 100px)' }}>

      {/* === EN-TÊTE === */}
      <div className="px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center gap-3 flex-shrink-0">
        <SentinelleAvatar size="md" isThinking={chatLoading} />
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold text-gray-900 text-lg">SENTINELLE</h1>
          <p className="text-xs text-gray-500">
            {chatLoading ? 'SENTINELLE réfléchit...' : 'Votre copilote conseil syndical'}
          </p>
        </div>
        <button
          onClick={resetChat}
          className="inline-flex items-center gap-1.5 text-sm text-blue-600 font-medium px-3 py-1.5 rounded-lg bg-white border border-blue-200 hover:bg-blue-50 hover:border-blue-300 transition-colors shadow-sm"
          title="Nouvelle conversation"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Nouveau chat
        </button>
      </div>

      {/* === ZONE DE MESSAGES (scrollable) === */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-5 space-y-4"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Overlay drag'n'drop */}
        {isDragOver && (
          <div className="fixed inset-0 z-50 bg-blue-500/10 backdrop-blur-sm flex items-center justify-center pointer-events-none">
            <div className="bg-white rounded-2xl shadow-2xl p-8 text-center border-2 border-dashed border-blue-400">
              <Upload className="w-12 h-12 text-blue-500 mx-auto mb-3" />
              <p className="text-lg font-semibold text-gray-800">Déposez votre document</p>
              <p className="text-sm text-gray-500 mt-1">PDF, images, Word, Excel — 25 Mo max</p>
            </div>
          </div>
        )}

        {/* Accueil si conversation vide */}
        {chatMessages.length === 0 && !chatLoading && !analyseEnCours && !resultatAnalyse && (
          <div className="flex flex-col items-center justify-center py-8 px-4">

            {/* Héro */}
            <div className="relative mb-6">
              <div className="absolute inset-0 -m-6 bg-gradient-to-br from-blue-100/60 via-indigo-50/40 to-transparent rounded-full blur-2xl" />
              <SentinelleAvatar size="xl" className="relative z-10" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">
              Bonjour {prenom} !
            </h2>
            <p className="text-sm text-gray-500 mb-8 max-w-md text-center">
              Comment puis-je vous aider aujourd'hui ?
            </p>

            {/* Suggestions dynamiques */}
            {suggestionsLoading ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Analyse de la situation en cours...</span>
              </div>
            ) : (
              <div className="w-full max-w-2xl flex flex-col gap-2">
                {suggestionsAccueil.map((suggestion, i) => (
                  <button
                    key={`gen-${i}`}
                    onClick={() => envoyerMessageChat(suggestion)}
                    className={`text-sm px-4 py-2.5 rounded-xl border transition-all text-left ${
                      i === 0
                        ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 font-medium shadow-sm'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                    }`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}

            {/* Indication drop document */}
            <div className="mt-8 flex items-center gap-2 text-gray-400 text-xs">
              <Paperclip className="w-3.5 h-3.5" />
              <span>Vous pouvez glisser un document ici ou utiliser le trombone ci-dessous</span>
            </div>
          </div>
        )}

        {/* Messages de la conversation */}
        {chatMessages.map((msg) => (
          <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
            {msg.role === 'assistant' ? (
              <SentinelleAvatar size="sm" className="flex-shrink-0 mt-1" />
            ) : (
              <div className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white text-xs font-bold">
                  {userProfile?.prenom?.charAt(0)?.toUpperCase() || 'C'}
                </span>
              </div>
            )}

            <div className={`max-w-[75%] ${msg.role === 'user' ? 'text-right' : ''}`}>
              <div className={`inline-block rounded-2xl px-4 py-2.5 text-sm ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white rounded-tr-sm'
                  : 'bg-gray-100 text-gray-800 rounded-tl-sm'
              }`}>
                <div className={msg.role === 'user' ? 'whitespace-pre-wrap' : 'prose prose-sm max-w-none'}>
                  {msg.role === 'user' ? msg.content : (() => {
                    // Session 222 : Détecter liste numérotée en fin de message → chips cliquables
                    if (msg.actions && msg.actions.length > 0) return renderMarkdown(msg.content);
                    const lines = msg.content.trimEnd().split('\n');
                    const chips: string[] = [];
                    let i = lines.length - 1;
                    while (i >= 0) {
                      const t = lines[i].trim();
                      if (/^\d+\.\s+/.test(t)) {
                        chips.unshift(t.replace(/^\d+\.\s+/, '').split(' - ')[0].trim());
                        i--;
                      } else if (t === '') { i--; } else { break; }
                    }
                    if (chips.length < 2) return renderMarkdown(msg.content);
                    const contentSansListe = lines.slice(0, i + 1).join('\n').trimEnd();
                    return (
                      <>
                        {renderMarkdown(contentSansListe)}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {chips.map((chip, idx) => (
                            <button
                              key={idx}
                              onClick={() => setChatInput(chip)}
                              className="text-xs px-3 py-1.5 rounded-full border border-blue-300 bg-white text-blue-700 hover:bg-blue-50 hover:border-blue-400 transition-colors cursor-pointer font-medium"
                            >
                              {chip}
                            </button>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Incitation pièces */}
              {msg.incitationPieces && (
                <div className="mt-1.5 text-xs text-amber-600 italic px-1">
                  {msg.incitationPieces}
                </div>
              )}

              {/* Actions proposées */}
              {msg.actions && msg.actions.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {msg.actions.map((action) => (
                    <button
                      key={action.id}
                      onClick={() => handleAction(action, msg.courrier)}
                      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                        action.type === 'generate_courrier'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : action.type === 'send_email'
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : action.type === 'create_affaire'
                              ? 'bg-amber-500 text-white hover:bg-amber-600'
                              : action.type === 'create_signalement'
                                ? 'bg-red-500 text-white hover:bg-red-600'
                                : action.type === 'update_paiement'
                                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                  : action.type === 'create_evenement'
                                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {action.type === 'generate_courrier' && <FileText className="w-3.5 h-3.5" />}
                      {action.type === 'send_email' && <Send className="w-3.5 h-3.5" />}
                      {action.type === 'create_signalement' && <AlertTriangle className="w-3.5 h-3.5" />}
                      {action.type === 'create_affaire' && <FolderOpen className="w-3.5 h-3.5" />}
                      {action.type === 'update_paiement' && <CreditCard className="w-3.5 h-3.5" />}
                      {action.type === 'create_evenement' && <Calendar className="w-3.5 h-3.5" />}
                      {action.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Bouton export PDF */}
              {msg.role === 'assistant' && isResultatStructure(msg.content) && (
                <div className="mt-2">
                  <button
                    onClick={async () => {
                      let nomCopro = 'Copropriété';
                      if (coproprieteId) {
                        try {
                          const coproSnap = await getDoc(doc(db, 'coproprietes', coproprieteId));
                          if (coproSnap.exists()) {
                            nomCopro = coproSnap.data().nom || nomCopro;
                          }
                        } catch { /* silencieux */ }
                      }
                      telechargerPDFSentinelle({
                        contenu: msg.content,
                        nomCopropriete: nomCopro,
                        nomUtilisateur: `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim() || 'Utilisateur',
                        dateMessage: msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp),
                      });
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700 border border-gray-200 hover:border-amber-300 transition-colors"
                    title="Exporter cette analyse en PDF"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Exporter en PDF
                  </button>
                </div>
              )}

              {/* Suggestions de réponse cliquables */}
              {msg.suggestionsReponse && msg.suggestionsReponse.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {msg.suggestionsReponse.map((suggestion, idx) => (
                    <button
                      key={`sug_${msg.id}_${idx}`}
                      onClick={() => envoyerMessageChat(suggestion)}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Indicateur de chargement chat */}
        {chatLoading && (
          <div className="flex gap-3">
            <SentinelleAvatar size="sm" isThinking className="flex-shrink-0 mt-1" />
            <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {/* Analyse en cours */}
        {analyseEnCours && (
          <div className="flex gap-3">
            <SentinelleAvatar size="sm" isThinking className="flex-shrink-0 mt-1" />
            <div className="bg-blue-50 border border-blue-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[75%]">
              <div className="flex items-center gap-2">
                <div className="relative w-5 h-5">
                  <div className="absolute inset-0 rounded-full border-2 border-blue-200" />
                  <div className="absolute inset-0 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                </div>
                <span className="text-sm text-blue-700">{etapeAnalyse}</span>
              </div>
            </div>
          </div>
        )}

        {/* Erreur analyse */}
        {erreurAnalyse && !analyseEnCours && (
          <div className="flex gap-3">
            <SentinelleAvatar size="sm" className="flex-shrink-0 mt-1" />
            <div className="bg-red-50 border border-red-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[75%]">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">Analyse impossible</p>
                  <p className="text-xs text-red-600 mt-0.5">{erreurAnalyse}</p>
                  <button
                    onClick={() => setErreurAnalyse(null)}
                    className="mt-2 text-xs text-red-600 hover:text-red-800 font-medium"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Résultat analyse — Doublon détecté */}
        {resultatAnalyse && !analyseEnCours && doublonDetecte && doublonDetecte.isDuplicate && !resultatAnalyse.fileUrl && (
          <div className="flex gap-3">
            <SentinelleAvatar size="sm" className="flex-shrink-0 mt-1" />
            <div className="bg-amber-50 border border-amber-300 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[75%]">
              <div className="flex items-start gap-2">
                <Copy className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Document déjà archivé</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Ce fichier (<span className="font-medium">{resultatAnalyse.fileName}</span>) est identique à un document existant :
                  </p>
                  <p className="text-sm font-medium text-amber-900 mt-1.5">
                    "{doublonDetecte.existingDocumentTitre}"
                    {doublonDetecte.existingDocumentDate && (
                      <span className="font-normal text-amber-700">
                        {' '}— archivé le {doublonDetecte.existingDocumentDate.toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={confirmerArchivageMalgreDoublon}
                      disabled={archivageEnCours}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700 disabled:opacity-50 transition-colors"
                    >
                      {archivageEnCours ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                      Analyser et archiver quand même
                    </button>
                    <button
                      onClick={fermerResultat}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-gray-600 border border-gray-200 rounded-lg text-xs hover:bg-gray-50 transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Résultat analyse — Normal */}
        {resultatAnalyse && !analyseEnCours && resultatAnalyse.fileUrl && (
          <div className="flex gap-3">
            <SentinelleAvatar size="sm" className="flex-shrink-0 mt-1" />
            <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] shadow-sm">

              {/* Document hors périmètre */}
              {resultatAnalyse.suggestion.typeDocument === 'hors_perimetre' ? (
                <>
                  <div className="flex items-start gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-orange-800">Document hors périmètre</p>
                      <p className="text-xs text-gray-500">{resultatAnalyse.fileName}</p>
                    </div>
                    <button onClick={fermerResultat} className="text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">
                    {resultatAnalyse.suggestion.explication || 'Ce document n\'a pas de rapport avec la gestion de copropriété.'}
                  </p>
                  <button
                    onClick={fermerResultat}
                    className="w-full px-4 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
                  >
                    Compris
                  </button>
                </>
              ) : (
                <>
                  {/* En-tête */}
                  <div className="flex items-start gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800">Document analysé</p>
                      <p className="text-xs text-gray-500">{resultatAnalyse.fileName}</p>
                    </div>
                    <button onClick={fermerResultat} className="text-gray-400 hover:text-gray-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Détails */}
                  <div className="space-y-1.5 text-sm mb-3">
                    <div className="flex gap-2">
                      <span className="text-gray-500 w-20 flex-shrink-0">Titre :</span>
                      <span className="font-medium text-gray-900">{resultatAnalyse.suggestion.titre}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-gray-500 w-20 flex-shrink-0">Type :</span>
                      <span className="text-gray-700">{resultatAnalyse.suggestion.typeDocumentLabel}</span>
                    </div>
                    {resultatAnalyse.suggestion.categorieLabel && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 w-20 flex-shrink-0">Catégorie :</span>
                        <span className="text-gray-700">{resultatAnalyse.suggestion.categorieLabel}</span>
                      </div>
                    )}
                    {resultatAnalyse.suggestion.explication && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 w-20 flex-shrink-0">Analyse :</span>
                        <span className="text-gray-700">{resultatAnalyse.suggestion.explication}</span>
                      </div>
                    )}
                    {resultatAnalyse.suggestion.actionsRecommandees && resultatAnalyse.suggestion.actionsRecommandees.length > 0 && (
                      <div className="flex gap-2">
                        <span className="text-gray-500 w-20 flex-shrink-0">Actions :</span>
                        <div className="space-y-0.5">
                          {resultatAnalyse.suggestion.actionsRecommandees.map((action: string, idx: number) => (
                            <p key={idx} className="text-gray-700 text-sm">• {action}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Intelligence Correspondance — alerte qualité */}
                  {resultatAnalyse.suggestion.courrierRef_lie && (
                    <div className={`rounded-lg p-3 mb-3 border ${
                      resultatAnalyse.suggestion.qualiteReponse === 'complete'
                        ? 'bg-green-50 border-green-200'
                        : resultatAnalyse.suggestion.qualiteReponse === 'partielle'
                          ? 'bg-amber-50 border-amber-200'
                          : 'bg-red-50 border-red-200'
                    }`}>
                      <p className="text-xs font-semibold mb-1 text-gray-700">Intelligence Correspondance</p>
                      {resultatAnalyse.suggestion.alerteQualite && (
                        <p className="text-xs text-gray-600">{resultatAnalyse.suggestion.alerteQualite}</p>
                      )}
                    </div>
                  )}

                  {/* Boutons d'action */}
                  <div className="flex flex-col gap-2">
                    {/* Archiver = action principale dans tous les cas */}
                    <div className="flex gap-2">
                      <button
                        onClick={archiverDocument}
                        disabled={archivageEnCours}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-lg font-medium text-sm hover:bg-amber-600 disabled:opacity-50 transition-colors"
                      >
                        {archivageEnCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                        {resultatAnalyse.suggestion.pointsManquants && resultatAnalyse.suggestion.pointsManquants.length > 0
                          ? 'Archiver seulement'
                          : 'Archiver dans les documents'}
                      </button>
                      <button
                        onClick={fermerResultat}
                        className="px-4 py-2.5 text-gray-600 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                      >
                        Ignorer
                      </button>
                    </div>

                    {/* Relance si points manquants = action secondaire */}
                    {resultatAnalyse.suggestion.pointsManquants && resultatAnalyse.suggestion.pointsManquants.length > 0 && (
                      <button
                        onClick={async () => {
                          if (!resultatAnalyse || !coproprieteId || !firebaseUser) return;
                          setArchivageEnCours(true);
                          try {
                            const { suggestion, fileName, fileUrl, fileType, fileSize, fileHash } = resultatAnalyse;
                            const userNom = `${userProfile?.prenom || ''} ${userProfile?.nom || ''}`.trim();
                            await uploadDocument({
                              coproprieteId,
                              titre: suggestion.titre,
                              type: suggestion.typeDocument,
                              categorie: suggestion.categorie || 'administratif',
                              description: suggestion.description || undefined,
                              dateDocument: suggestion.dateDocument ? new Date(suggestion.dateDocument) : undefined,
                              interlocuteurId: suggestion.interlocuteurId || undefined,
                              interlocuteurNom: suggestion.interlocuteurNom || undefined,
                              affaireId: suggestion.affaireId || undefined,
                              visibilite: 'proprietaire',
                              userId: firebaseUser.uid,
                              userNom,
                              fileHash,
                              tempFilePath: fileUrl,
                              fichierNom: fileName,
                              fichierTaille: fileSize,
                              fichierType: fileType,
                              source: 'sentinelle_cs',
                              typeDocumentLabel: suggestion.typeDocumentLabel,
                              donneesCles: suggestion.donneesCles || {},
                              anomalies: suggestion.anomalies || [],
                              analyseSentinelle: {
                                confiance: suggestion.confidence,
                                explication: suggestion.explication,
                                actionsRecommandees: suggestion.actionsRecommandees,
                                anomalies: suggestion.anomalies || [],
                                donneesCles: suggestion.donneesCles || {},
                                dateAnalyse: Timestamp.now()
                              },
                              sensCorrespondance: suggestion.sensCorrespondance || null,
                              courrierId_lie: suggestion.courrierId_lie || undefined,
                              courrierRef_lie: suggestion.courrierRef_lie || undefined,
                              typeCorrespondance: suggestion.typeCorrespondance || undefined,
                              qualiteReponse: suggestion.qualiteReponse || undefined,
                              pointsTraites: suggestion.pointsTraites || undefined,
                              pointsManquants: suggestion.pointsManquants || undefined,
                              alerteQualite: suggestion.alerteQualite || undefined,
                            });

                            const contenuRelance = suggestion.contenuRelanceSCUD
                              || `Madame, Monsieur,\n\nJe fais suite à votre réponse concernant notre échange récent.\n\nPourriez-vous nous apporter des précisions sur les points suivants :\n\n${suggestion.pointsManquants?.map((p: string) => `- ${p}`).join('\n') || ''}\n\nJe vous remercie par avance.\n\nCordialement.`;
                            const objetRelance = suggestion.objetRelanceSCUD
                              || `Relance - ${suggestion.titre?.replace('Réponse du syndic - ', '').replace('Réponse syndic - ', '') || 'notre échange'}`;

                            navigate('/admin/courriers/nouveau', {
                              state: {
                                fromSentinelle: true,
                                prefill: {
                                  objet: objetRelance,
                                  contenu: contenuRelance,
                                  type: 'relance',
                                  destinataire: suggestion.interlocuteurNom || '',
                                  destinataireType: 'syndic',
                                  modeEnvoi: 'email',
                                  // Session 216 : Rattachement au courrier d'origine pour calcul niveau relance
                                  courrierId_lie: suggestion.courrierId_lie || undefined,
                                }
                              }
                            });
                          } catch (error) {
                            console.error('[SENTINELLE CS] Erreur archivage+relance:', error);
                            const errMsg = error instanceof Error ? error.message : '';
                            if (errMsg.startsWith('DOUBLON_DETECTE|')) {
                              setErreurAnalyse(errMsg.split('|')[1] || 'Ce document existe déjà.');
                            } else {
                              setErreurAnalyse('Erreur lors de l\'archivage. Réessayez.');
                            }
                            setArchivageEnCours(false);
                          }
                        }}
                        disabled={archivageEnCours}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 disabled:opacity-50 transition-colors"
                      >
                        {archivageEnCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Archiver et rédiger une relance ({resultatAnalyse.suggestion.pointsManquants.length} point{resultatAnalyse.suggestion.pointsManquants.length > 1 ? 's' : ''})
                      </button>
                    )}

                    {/* Session 216 : Affichage niveau de relance calculé côté serveur */}
                    {resultatAnalyse.suggestion.niveauRelance && resultatAnalyse.suggestion.pointsManquants && resultatAnalyse.suggestion.pointsManquants.length > 0 && (
                      <p className="text-xs text-center text-gray-500 mt-1">
                        Niveau détecté : <span className="font-medium text-gray-700">{resultatAnalyse.suggestion.niveauRelance}</span>
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Erreur chat */}
        {chatError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p>{chatError}</p>
              <button onClick={() => setChatError(null)} className="text-red-600 font-medium mt-1 hover:underline">
                Fermer
              </button>
            </div>
          </div>
        )}

        {/* Ancre scroll */}
        <div ref={chatEndRef} />
      </div>

      {/* === ZONE DE SAISIE + TROMBONE === */}
      <div className="border-t border-gray-100 p-4 flex-shrink-0 bg-white">
        <div className="flex items-end gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={analyseEnCours}
            className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors disabled:opacity-50 flex-shrink-0"
            title="Joindre un document"
          >
            <Paperclip className="w-5 h-5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
            className="hidden"
          />

          <BoutonDictee
            onTranscript={(text) => {
              setChatInput(prev => prev ? prev + ' ' + text : text);
              const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
              if (textarea) textarea.placeholder = 'Décrivez votre situation ou déposez un document...';
            }}
            onInterim={(text) => {
              const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
              if (textarea) textarea.placeholder = text + '...';
            }}
            disabled={chatLoading}
          />

          <textarea
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={handleChatKeyDown}
            placeholder="Décrivez votre situation ou déposez un document..."
            rows={1}
            disabled={chatLoading}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:opacity-50 transition-colors"
            style={{ maxHeight: '120px', minHeight: '42px' }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = '42px';
              target.style.height = Math.min(target.scrollHeight, 120) + 'px';
            }}
          />

          <button
            onClick={() => envoyerMessageChat(chatInput)}
            disabled={!chatInput.trim() || chatLoading}
            className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            {chatLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SentinelleCSPage;
